import { useState } from "react";
import type { ContractIn } from "../lib/types";

interface Props {
  onImport: (c: ContractIn) => void;
  onClose: () => void;
}

const PROMPT_TEMPLATE = `Lees de bijgevoegde offerte (PDF, foto, of tekst) en extract de tariefgegevens.
Geef ALLEEN een JSON-object terug, geen extra tekst, met dit exacte schema:

{
  "name": "Leverancier + producttype",
  "type": "vast" of "dynamisch",
  "notes": "korte notitie met bron en datum",
  "vastrecht_eur_per_maand": <getal>,
  "energiebelasting_eur_per_kwh": 0.0916,
  "btw_pct": 21,
  "kale_prijs_afname_eur_per_kwh": <getal of null bij dynamisch>,
  "terugleververgoeding_eur_per_kwh": <getal of null bij dynamisch>,
  "inkoopvergoeding_eur_per_kwh": <getal, default 0>,
  "inkoopvergoeding_handling": "uitgekeerd" | "neutraal" | "afgetrokken",
  "terugleverkosten_model": "geen" | "vast" | "per_kwh",
  "terugleverkosten_vast_eur_per_maand": <getal of 0>,
  "terugleverkosten_per_kwh": <getal of 0>,
  "niet_saldering_pct": 50
}

Belangrijke definities:
- "kale_prijs_afname": leveringsprijs per kWh excl. belasting (alleen voor vast contract)
- "terugleververgoeding": wat je krijgt voor teruglevering boven saldeerplafond (alleen vast)
- "inkoopvergoeding": opslag van leverancier op import, en eventueel bonus/kost op export
- "inkoopvergoeding_handling": wat de leverancier doet met de inkoopvergoeding bij teruglevering
- "terugleverkosten_model": "vast" = vast bedrag/maand, "per_kwh" = per teruggeleverde kWh, "geen" = niets
- "niet_saldering_pct": voor 2027+, wat is de gegarandeerde minimum-vergoeding als % van kale prijs (default 50)`;

export function ImportContractModal({ onImport, onClose }: Props) {
  const [json, setJson] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleParse = () => {
    try {
      const parsed = JSON.parse(json);
      // Minimal validation — backend doet de echte schema check
      if (!parsed.name || !parsed.type) {
        setError("JSON moet minimaal 'name' en 'type' bevatten");
        return;
      }
      onImport(parsed as ContractIn);
    } catch (e) {
      setError("Ongeldige JSON: " + (e as Error).message);
    }
  };

  const copyPrompt = () => {
    navigator.clipboard.writeText(PROMPT_TEMPLATE);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      style={{ background: "rgba(20, 20, 20, 0.4)" }}
      onClick={onClose}
    >
      <div
        className="bg-paper rounded-t-2xl md:rounded-2xl max-w-[700px] w-full max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <h2 className="serif m-0 text-[22px] font-medium">Contract importeren via Claude AI</h2>
          <button className="btn btn-ghost" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="help mb-4">
          Geef een offerte aan Claude (chat.claude.ai of mobile app) met onderstaande prompt.
          Claude geeft je een JSON terug — plak die hieronder en klik Importeren.
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-1.5">
            <span className="label-up">1. Prompt voor Claude</span>
            <button className="btn text-xs" onClick={copyPrompt}>
              Kopieer
            </button>
          </div>
          <pre className="bg-bg p-3 rounded-md text-xs overflow-x-auto whitespace-pre-wrap mono max-h-[200px] overflow-y-auto">
            {PROMPT_TEMPLATE}
          </pre>
        </div>

        <div className="mb-4">
          <span className="label-up block mb-1.5">2. Plak Claude's JSON antwoord</span>
          <textarea
            className="input mono"
            rows={10}
            value={json}
            onChange={(e) => {
              setJson(e.target.value);
              setError(null);
            }}
            placeholder='{ "name": "...", "type": "dynamisch", ... }'
          />
        </div>

        {error && (
          <div className="bg-warn-bg text-warn p-3 rounded-md mb-3 text-sm">{error}</div>
        )}

        <div className="flex justify-end gap-2">
          <button className="btn" onClick={onClose}>
            Annuleren
          </button>
          <button className="btn btn-primary" onClick={handleParse} disabled={!json.trim()}>
            Importeren
          </button>
        </div>
      </div>
    </div>
  );
}
